import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { useState, useEffect } from 'react';
import toast, { Toaster } from 'react-hot-toast';

import type { MenuItem, ApiError } from '../../services/api';
import api from '../../services/api';

import styles from './MenuEditor.module.css';

/**
 * Helper function to extract error message from various error types
 * @param err - Error object (could be ApiError, Error, or unknown)
 * @param fallback - Fallback message if extraction fails
 */
const getErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === 'object' && 'message' in err) {
    return String((err as ApiError).message);
  }
  if (err instanceof Error) {
    return err.message;
  }
  return fallback;
};

/**
 * MenuEditor Component
 * Manages circular menu items with CRUD operations and drag-and-drop reordering
 */

// Sortable Menu Item Card Component
/**
 *
 */
type MenuItemCardProps = {
  readonly item: MenuItem;
  readonly onEdit: (item: MenuItem) => void;
  readonly onDelete: (id: string) => void;
  readonly onToggleEnabled: (id: string, enabled: boolean) => void;
};

const MenuItemCard = ({
  item,
  onEdit,
  onDelete,
  onToggleEnabled,
}: MenuItemCardProps): JSX.Element => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item._id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className={styles.menuItemCard}>
      <div className={styles.dragHandle} {...attributes} {...listeners}>
        ⋮⋮
      </div>
      <div className={styles.itemContent}>
        <div className={styles.itemHeader}>
          <span className={styles.itemOrder}>{item.order}</span>
          <span className={styles.itemLabel}>{item.label}</span>
          {item.hasSubmenu ? <span className={styles.badge}>Has Submenu</span> : null}
          {!item.enabled && <span className={styles.badgeDisabled}>Disabled</span>}
        </div>
        <div className={styles.itemDetails}>
          <span className={styles.itemSlug}>Slug: {item.slug}</span>
        </div>
      </div>
      <div className={styles.itemActions}>
        <label className={styles.toggleSwitch}>
          <input
            type="checkbox"
            checked={item.enabled}
            onChange={(e) => onToggleEnabled(item._id, e.target.checked)}
          />
          <span className={styles.slider}></span>
        </label>
        <button onClick={() => onEdit(item)} className={styles.editButton} title="Edit">
          ✏️
        </button>
        <button onClick={() => onDelete(item._id)} className={styles.deleteButton} title="Delete">
          🗑️
        </button>
      </div>
    </div>
  );
};

// Menu Item Form Component
/**
 *
 */
type MenuItemFormProps = {
  readonly item: MenuItem | null;
  readonly onSave: (data: Partial<MenuItem>) => Promise<void>;
  readonly onCancel: () => void;
};

const MenuItemForm = ({ item, onSave, onCancel }: MenuItemFormProps): JSX.Element => {
  const [formData, setFormData] = useState<Partial<MenuItem> & { readonly pageType?: string }>({
    label: item?.label ?? '',
    slug: item?.slug ?? '',
    order: item?.order ?? 1,
    enabled: item?.enabled ?? true,
    hasSubmenu: item?.hasSubmenu ?? false,
    pageType: 'text-cloud', // Default page type for new items
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [existingPageType, setExistingPageType] = useState<string | null>(null);
  const [loadingPageType, setLoadingPageType] = useState(false);

  // Fetch page type for existing items
  useEffect(() => {
    if (item && item.slug) {
      setLoadingPageType(true);
      api.content
        .getBySlug(item.slug)
        .then((page) => {
          setExistingPageType(page.type);
        })
        .catch((err) => {
          console.error('Failed to load page type:', err);
          setExistingPageType(null);
        })
        .finally(() => {
          setLoadingPageType(false);
        });
    }
  }, [item]);

  // Helper to check if current page type supports submenus (categories)
  const isGalleryType = (type?: string): boolean => {
    return type === 'image-gallery' || type === 'shop-gallery';
  };

  // Get the current page type (either from form for new items or fetched for existing items)
  const currentPageType = item ? existingPageType : formData.pageType;

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.label || formData.label.trim().length === 0) {
      newErrors.label = 'Label is required';
    } else if (formData.label.length > 50) {
      newErrors.label = 'Label cannot exceed 50 characters';
    }

    if (!formData.slug || formData.slug.trim().length === 0) {
      newErrors.slug = 'Slug is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Slug must contain only lowercase letters, numbers, and hyphens';
    }

    if (!formData.order || formData.order < 1) {
      newErrors.order = 'Order must be at least 1';
    }

    // Only validate pageType for new items (not when editing)
    if (!item && !formData.pageType) {
      newErrors.pageType = 'Page type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setSaving(true);
    try {
      // When editing (item exists), exclude slug from the update to prevent changes
      // Also set hasSubmenu to false if page type doesn't support it
      const shouldForceNoSubmenu = !isGalleryType(currentPageType ?? undefined);
      const dataToSave = item
        ? {
            ...formData,
            slug: undefined,
            hasSubmenu: shouldForceNoSubmenu ? false : formData.hasSubmenu,
          }
        : { ...formData, hasSubmenu: shouldForceNoSubmenu ? false : formData.hasSubmenu };

      await onSave(dataToSave);
    } catch {
      // Error handled in parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onCancel}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>{item ? 'Edit Menu Item' : 'Add New Menu Item'}</h2>
          <button onClick={onCancel} className={styles.closeButton}>
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="label">
              Label <span className={styles.required}>*</span>
            </label>
            <input
              id="label"
              type="text"
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              className={errors.label ? styles.inputError : ''}
              maxLength={50}
            />
            {errors.label ? <span className={styles.errorText}>{errors.label}</span> : null}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="slug">
              Slug <span className={styles.required}>*</span>
            </label>
            <input
              id="slug"
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase() })}
              className={errors.slug ? styles.inputError : ''}
              placeholder="e.g., about-me"
              disabled={!!item}
              readOnly={!!item}
            />
            {item ? (
              <small className={styles.helpText}>
                Slug cannot be changed for existing menu items (it identifies the page content)
              </small>
            ) : null}
            {errors.slug ? <span className={styles.errorText}>{errors.slug}</span> : null}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="order">
              Order <span className={styles.required}>*</span>
            </label>
            <input
              id="order"
              type="number"
              min="1"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 1 })}
              className={errors.order ? styles.inputError : ''}
            />
            {errors.order ? <span className={styles.errorText}>{errors.order}</span> : null}
          </div>

          {/* Only show page type selector when creating new items */}
          {!item && (
            <div className={styles.formGroup}>
              <label htmlFor="pageType">
                Page Type <span className={styles.required}>*</span>
              </label>
              <select
                id="pageType"
                value={formData.pageType}
                onChange={(e) => setFormData({ ...formData, pageType: e.target.value })}
                className={errors.pageType ? styles.inputError : ''}
              >
                <option value="text-cloud">Text Cloud</option>
                <option value="image-gallery">Image Gallery</option>
                <option value="icon-ring">Icon Ring</option>
                <option value="shop-gallery">Shop Gallery</option>
                <option value="blog">Blog</option>
              </select>
              {errors.pageType ? <span className={styles.errorText}>{errors.pageType}</span> : null}
              <small className={styles.helpText}>
                Select the type of page that will be created with this menu item
              </small>
            </div>
          )}

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.enabled ?? true}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              />
              <span>Enabled</span>
            </label>
          </div>

          {/* Only show Has Submenu checkbox for gallery pages (image-gallery, shop-gallery) */}
          {item ? (
            // For existing items, show only if page type is loaded and is a gallery type
            loadingPageType ? (
              <div className={styles.formGroup}>
                <small className={styles.helpText}>Loading page information...</small>
              </div>
            ) : isGalleryType(currentPageType ?? undefined) ? (
              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.hasSubmenu ?? false}
                    onChange={(e) => setFormData({ ...formData, hasSubmenu: e.target.checked })}
                  />
                  <span>Has Submenu (Category Filter)</span>
                </label>
                <small className={styles.helpText}>
                  Show category filter submenu for this gallery page
                </small>
              </div>
            ) : null
          ) : isGalleryType(formData.pageType) ? (
            // For new items, show only if selected page type is a gallery type
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.hasSubmenu ?? false}
                  onChange={(e) => setFormData({ ...formData, hasSubmenu: e.target.checked })}
                />
                <span>Has Submenu (Category Filter)</span>
              </label>
              <small className={styles.helpText}>
                Show category filter submenu for this gallery page
              </small>
            </div>
          ) : null}

          <div className={styles.modalActions}>
            <button
              type="button"
              onClick={onCancel}
              className={styles.cancelButton}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className={styles.saveButton} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main MenuEditor Component
const MenuEditor = (): JSX.Element => {
  const [menuItems, setMenuItems] = useState<ReadonlyArray<MenuItem>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchMenuItems();
  }, []);

  const fetchMenuItems = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const items = await api.menu.getAll();
      // Sort by order
      const sortedItems = [...items].sort((a, b) => a.order - b.order);
      setMenuItems(sortedItems);
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to load menu items');
      setError(errorMessage);
      console.error('Error fetching menu items:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data: Partial<MenuItem>): Promise<void> => {
    try {
      const newItem = await api.menu.create(data);
      setMenuItems([...menuItems, newItem].sort((a, b) => a.order - b.order));
      setIsModalOpen(false);
      setEditingItem(null);
      toast.success('Menu item created successfully');
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to create menu item');
      toast.error(errorMessage);
      console.error('Error creating menu item:', err);
      throw err;
    }
  };

  const handleUpdate = async (id: string, data: Partial<MenuItem>): Promise<void> => {
    try {
      const updatedItem = await api.menu.update(id, data);
      setMenuItems(
        menuItems
          .map((item) => (item._id === id ? updatedItem : item))
          .sort((a, b) => a.order - b.order)
      );
      setIsModalOpen(false);
      setEditingItem(null);
      toast.success('Menu item updated successfully');
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to update menu item');
      toast.error(errorMessage);
      console.error('Error updating menu item:', err);
      throw err;
    }
  };

  const handleDelete = async (id: string): Promise<void> => {
    const item = menuItems.find((m) => m._id === id);
    if (!item) return;

    if (!window.confirm(`Delete menu item "${item.label}"?`)) {
      return;
    }

    try {
      await api.menu.delete(id);
      setMenuItems(menuItems.filter((item) => item._id !== id));
      toast.success('Menu item deleted successfully');
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to delete menu item');
      toast.error(errorMessage);
      console.error('Error deleting menu item:', err);
    }
  };

  const handleToggleEnabled = async (id: string, enabled: boolean): Promise<void> => {
    try {
      const updatedItem = await api.menu.update(id, { enabled });
      setMenuItems(menuItems.map((item) => (item._id === id ? updatedItem : item)));
      toast.success(`Menu item ${enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to toggle menu item');
      toast.error(errorMessage);
      console.error('Error toggling menu item:', err);
    }
  };

  const handleDragEnd = async (event: DragEndEvent): Promise<void> => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = menuItems.findIndex((item) => item._id === active.id);
    const newIndex = menuItems.findIndex((item) => item._id === over.id);

    const newItems = arrayMove([...menuItems], oldIndex, newIndex);

    // Update orders
    const updatedItems = newItems.map((item, index) => ({
      ...item,
      order: index + 1,
    }));

    // Optimistically update UI
    setMenuItems(updatedItems);

    // Persist to backend
    try {
      const reorderData = updatedItems.map((item) => ({
        id: item._id,
        order: item.order,
      }));
      await api.menu.reorder(reorderData);
      toast.success('Menu order updated');
    } catch (err) {
      // Revert on error
      setMenuItems(menuItems);
      const errorMessage = getErrorMessage(err, 'Failed to reorder menu items');
      toast.error(errorMessage);
      console.error('Error reordering menu items:', err);
    }
  };

  const handleAddNew = (): void => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item: MenuItem): void => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleSave = async (data: Partial<MenuItem>): Promise<void> => {
    if (editingItem) {
      await handleUpdate(editingItem._id, data);
    } else {
      await handleCreate(data);
    }
  };

  return (
    <div className={styles.menuEditor}>
      <Toaster position="top-right" />

      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Menu Editor</h1>
          <p className={styles.subtitle}>Manage circular menu items</p>
        </div>
        <button onClick={handleAddNew} className={styles.addButton}>
          + Add New Menu Item
        </button>
      </div>

      {loading ? (
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading menu items...</p>
        </div>
      ) : null}

      {error ? (
        <div className={styles.error}>
          <p>❌ {error}</p>
          <button onClick={fetchMenuItems} className={styles.retryButton}>
            Retry
          </button>
        </div>
      ) : null}

      {!loading && !error && (
        <div className={styles.content}>
          {menuItems.length === 0 ? (
            <div className={styles.empty}>
              <p>No menu items yet. Click "Add New Menu Item" to create one.</p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={menuItems.map((item) => item._id || item.slug)}
                strategy={verticalListSortingStrategy}
              >
                <div className={styles.menuList}>
                  {menuItems.map((item) => (
                    <MenuItemCard
                      key={item._id || item.slug}
                      item={item}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onToggleEnabled={handleToggleEnabled}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}

      {isModalOpen ? (
        <MenuItemForm
          item={editingItem}
          onSave={handleSave}
          onCancel={() => {
            setIsModalOpen(false);
            setEditingItem(null);
          }}
        />
      ) : null}
    </div>
  );
};

export default MenuEditor;
